import { useContext, MouseEventHandler, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import { FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import { LayoutContext, ILayoutContext, DialogModesEnum } from '../';

export interface WrapFormViewProps {
  title?: string | null;
  item: any;
  onBack?: MouseEventHandler<HTMLButtonElement> | undefined;
  schema?: any | (() => any);
  view?: ((item: any) => ReactNode) | ((item: any, formik: any) => ReactNode);
  viewClassName?: string;
  onDelete?: (item: any) => Promise<void> | undefined;
  onSubmit?: (item: any) => Promise<void>;
  disableCreate?: boolean;
  disableUpdate?: boolean;
  disableDelete?: boolean;
  hideHeader?: boolean;
};

// WrapFormView wraps a given view and add validation and navigation bar/buttons to enable inserting and deleting items

const WrapFormView = (props: WrapFormViewProps) => {
  const layoutContext = useContext(LayoutContext) as ILayoutContext;

  const { title, item, schema, view, onBack, onDelete, onSubmit, disableCreate, disableUpdate, disableDelete, hideHeader } = props;
  const { t } = useTranslation();

  const confirmDelete = () => {
    layoutContext.dialog.show({
      title: t('delete-confirmation'),
      content: t('delete-confirmation-desc'),
      mode: DialogModesEnum.confirmation,
      onConfirm: async () => onDelete ? await onDelete(item) : undefined
    });
  };

  const formik = useFormik({
    initialValues: item,
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async (values) => {
      console.log('SUBMIT', values);
      if (onSubmit)
        await onSubmit(values);
    },
  });

  const { isValid, isSubmitting } = formik;

  return (
    <>
      {!hideHeader &&
        <div className='module-header'>
          {onBack &&
            <Button onClick={onBack} startIcon={<ChevronLeftIcon />}>
              {t('back')}
            </Button>}

          {title && <h2>{title}</h2>}

          {item.id &&
            <h3 className="item-id">ID: {item.id}</h3>}
        </div>}

      {view && <div className='module-view'>{
        view(item, formik)
      }</div>}

      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <div className='module-view-footer'>
            <div className='left'>
              {onDelete && item && (item as any).id && !disableDelete && (
                <Box margin={1}>
                  <Button
                    size='small'
                    variant='outlined'
                    className='danger-button'
                    endIcon={<DeleteIcon />}
                    onClick={confirmDelete}>
                    {t('delete')}
                  </Button>
                </Box>
              )}
            </div>

            <div className='right'>
              {onSubmit && (
                <Box margin={1}>
                  {(item as any).id && !disableUpdate &&
                    <Button
                      type='submit'
                      size='small'
                      endIcon={<SaveIcon />}
                      disabled={isSubmitting || !isValid}>
                      {t('save')}
                    </Button>}

                  {!(item as any).id && !disableCreate &&
                    <Button
                      type='submit'
                      size='small'
                      endIcon={<SaveIcon />}
                      disabled={isSubmitting || !isValid}>
                      {t('create')}
                    </Button>}
                </Box>
              )}
            </div>
          </div>
        </form>
      </FormikProvider>

      {isSubmitting && <LinearProgress />}
    </>
  );
};

export default WrapFormView;